import React, { useRef } from 'react';
import './YouTubeSection.css';

export interface YouTubeVideo {
  videoId: string;   // YouTube video ID (the part after ?v=)
  caption?: string;  // Optional caption shown below the thumbnail
}

interface YouTubeSectionProps {
  title?: string;
  subtitle?: string;
  videos: YouTubeVideo[];
}

const YouTubeSection: React.FC<YouTubeSectionProps> = ({
  title = 'Watch & Learn',
  subtitle = 'Discover the beauty and meaning behind our sacred ceremonies',
  videos,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const cardWidth = scrollRef.current.querySelector('.yt-card')?.clientWidth ?? 340;
    const gap = 20;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -(cardWidth + gap) : cardWidth + gap,
      behavior: 'smooth',
    });
  };

  if (!videos || videos.length === 0) return null;

  return (
    <section className="yt-section">
      <div className="yt-section-header">
        <h2 className="yt-title">{title}</h2>
        {subtitle && <p className="yt-subtitle">{subtitle}</p>}
      </div>

      <div className="yt-scroll-wrapper">
        {/* Left scroll button */}
        <button
          className="yt-scroll-btn yt-scroll-btn--left"
          onClick={() => scroll('left')}
          aria-label="Scroll left"
        >
          ‹
        </button>

        {/* Scrollable video row */}
        <div className="yt-track" ref={scrollRef}>
          {videos.map((video, index) => (
            <div className="yt-card" key={video.videoId + index}>
              <div className="yt-embed-wrapper">
                <iframe
                  src={`https://www.youtube.com/embed/${video.videoId}?rel=0&modestbranding=1`}
                  title={video.caption || `${title} – Video ${index + 1}`}
                  frameBorder="0"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              {video.caption && (
                <p className="yt-caption">{video.caption}</p>
              )}
            </div>
          ))}
        </div>

        {/* Right scroll button */}
        <button
          className="yt-scroll-btn yt-scroll-btn--right"
          onClick={() => scroll('right')}
          aria-label="Scroll right"
        >
          ›
        </button>
      </div>
    </section>
  );
};

export default YouTubeSection;
